import { makeAutoObservable, runInAction } from "mobx";
import AddressAPI from "../api/AddressAPI";
import OrderAPI from "../api/OrderAPI";
import { City } from "../../../models/Address/Address";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { SortType } from "../../../models/SortType";
import { OrderBasicDetails } from "../../../models/Order/OrderDetails";
import {
  DeliveryCityRequest,
  DomesticDeliveryFields,
  InternationalDeliveryFields,
  MAX_DELIVERY_LOCATION_COUNT,
  MIN_DELIVERY_LOCATION_COUNT,
  MIN_INTERNATIONAL_DELIVERY_COUNT,
  OrderDeliveryCity,
  OrderDeliveryDetails,
  OrderDeliveryRegion,
  OrderDeliveryType,
  UpdateOrderDeliveryRequest,
  deliveryRegionUsesDomestic,
  deliveryRegionUsesInternational,
  deliveryTypeUsesCities,
  deliveryTypeUsesRemoteCount,
  resolveDeliveryRegion,
} from "../../../models/Order/OrderDelivery";

const DEFAULT_LOCATION_COUNT = "1";
const DEFAULT_INTERNATIONAL_COUNT = "0";
const CITY_SEARCH_LIMIT = 50;

export interface ShippingCityDraft {
  city_id: number;
  city_name: string;
  subtitle?: string;
  location_count: string;
}

interface SavedDeliveryState {
  region: OrderDeliveryRegion | null;
  type: OrderDeliveryType | null;
  remoteLocationCount: number | null;
  cities: OrderDeliveryCity[];
  internationalRemoteCount: number | null;
  internationalBulkCount: number | null;
}

const parseCount = (value: string, min: number): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  if (parsed < min || parsed > MAX_DELIVERY_LOCATION_COUNT) return null;
  return parsed;
};

const parseLocationCount = (value: string): number | null =>
  parseCount(value, MIN_DELIVERY_LOCATION_COUNT);

const parseInternationalCount = (value: string): number | null =>
  parseCount(value, MIN_INTERNATIONAL_DELIVERY_COUNT);

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

export class OrderShippingDetailsStore {
  addressAPI: AddressAPI;
  orderAPI: OrderAPI;

  orderId: number | null = null;
  deliveryRegion: OrderDeliveryRegion | null = null;
  deliveryType: OrderDeliveryType | null = null;
  remoteLocationCount: string = DEFAULT_LOCATION_COUNT;
  cities: ShippingCityDraft[] = [];
  internationalRemoteCount: string = DEFAULT_INTERNATIONAL_COUNT;
  internationalBulkCount: string = DEFAULT_INTERNATIONAL_COUNT;

  cityOptions: City[] = [];
  isCitiesLoading: boolean = false;
  citiesError: string = "";

  isSaving: boolean = false;
  saveError: string = "";
  successMessage: string = "";

  private latestCitySearchId = 0;

  constructor() {
    this.addressAPI = new AddressAPI();
    this.orderAPI = new OrderAPI();
    makeAutoObservable<OrderShippingDetailsStore, "latestCitySearchId">(
      this,
      { addressAPI: false, orderAPI: false, latestCitySearchId: false },
    );
  }

  get usesDomestic(): boolean {
    return deliveryRegionUsesDomestic(this.deliveryRegion);
  }

  get usesInternational(): boolean {
    return deliveryRegionUsesInternational(this.deliveryRegion);
  }

  get usesRemoteCount(): boolean {
    return this.usesDomestic && deliveryTypeUsesRemoteCount(this.deliveryType);
  }

  get usesCities(): boolean {
    return this.usesDomestic && deliveryTypeUsesCities(this.deliveryType);
  }

  get remoteLocationTotal(): number {
    return this.usesRemoteCount
      ? (parseLocationCount(this.remoteLocationCount) ?? 0)
      : 0;
  }

  get cityLocationTotal(): number {
    if (!this.usesCities) return 0;
    return this.cities.reduce(
      (sum, city) => sum + (parseLocationCount(city.location_count) ?? 0),
      0,
    );
  }

  get domesticLocationTotal(): number {
    return this.remoteLocationTotal + this.cityLocationTotal;
  }

  get internationalRemoteTotal(): number {
    return this.usesInternational
      ? (parseInternationalCount(this.internationalRemoteCount) ?? 0)
      : 0;
  }

  get internationalBulkTotal(): number {
    return this.usesInternational
      ? (parseInternationalCount(this.internationalBulkCount) ?? 0)
      : 0;
  }

  get internationalLocationTotal(): number {
    return this.internationalRemoteTotal + this.internationalBulkTotal;
  }

  get totalLocationCount(): number {
    return this.domesticLocationTotal + this.internationalLocationTotal;
  }

  get selectedCityFilterValues(): FilterValue[] {
    return this.cities.map((city) => ({
      id: String(city.city_id),
      value: city.city_name,
      subtitle: city.subtitle,
    }));
  }

  get cityFilterOptions(): FilterValue[] {
    return this.cityOptions
      .filter((city) => city.id !== undefined)
      .map((city) => ({
        id: String(city.id),
        value: city.name ?? `City #${city.id}`,
        subtitle: [city.state, city.country].filter(Boolean).join(", "),
      }));
  }

  setOrderId = (orderId: number) => {
    this.orderId = Number.isFinite(orderId) && orderId > 0 ? orderId : null;
  };

  hydrate = (order: OrderBasicDetails) => {
    this.applyDeliveryState({
      region: resolveDeliveryRegion(order),
      type: order.delivery_type ?? null,
      remoteLocationCount: order.delivery_location_count ?? null,
      cities: order.delivery_cities ?? [],
      internationalRemoteCount:
        order.delivery_international_remote_count ?? null,
      internationalBulkCount: order.delivery_international_bulk_count ?? null,
    });
    this.clearMessages();
  };

  private applyDeliveryState = (state: SavedDeliveryState) => {
    const subtitleById = new Map(
      this.cities.map((city) => [city.city_id, city.subtitle]),
    );
    const hasDomestic = deliveryRegionUsesDomestic(state.region);
    const type = hasDomestic ? state.type : null;
    const cities = deliveryTypeUsesCities(type) ? state.cities : [];

    this.deliveryRegion = state.region;
    this.deliveryType = type;
    this.remoteLocationCount =
      deliveryTypeUsesRemoteCount(type) && state.remoteLocationCount
        ? String(state.remoteLocationCount)
        : DEFAULT_LOCATION_COUNT;
    this.cities = cities.map((city) => ({
      city_id: city.city_id,
      city_name: city.city_name ?? `City #${city.city_id}`,
      subtitle: subtitleById.get(city.city_id),
      location_count: String(city.location_count),
    }));
    this.internationalRemoteCount = String(
      state.internationalRemoteCount ?? DEFAULT_INTERNATIONAL_COUNT,
    );
    this.internationalBulkCount = String(
      state.internationalBulkCount ?? DEFAULT_INTERNATIONAL_COUNT,
    );
  };

  clearMessages = () => {
    this.saveError = "";
    this.successMessage = "";
  };

  setDeliveryRegion = (region: OrderDeliveryRegion | null) => {
    this.deliveryRegion = region;
    this.clearMessages();
  };

  setDeliveryType = (type: OrderDeliveryType | null) => {
    this.deliveryType = type;
    this.clearMessages();
  };

  setRemoteLocationCount = (value: string) => {
    this.remoteLocationCount = value;
    this.clearMessages();
  };

  setInternationalRemoteCount = (value: string) => {
    this.internationalRemoteCount = value;
    this.clearMessages();
  };

  setInternationalBulkCount = (value: string) => {
    this.internationalBulkCount = value;
    this.clearMessages();
  };

  setSelectedCities = (selected: FilterValue[]) => {
    const existingById = new Map(
      this.cities.map((city) => [String(city.city_id), city]),
    );
    this.cities = selected.map(
      (item) =>
        existingById.get(String(item.id)) ?? {
          city_id: Number(item.id),
          city_name: item.value,
          subtitle: item.subtitle,
          location_count: DEFAULT_LOCATION_COUNT,
        },
    );
    this.clearMessages();
  };

  setCityLocationCount = (cityId: number, value: string) => {
    this.cities = this.cities.map((city) =>
      city.city_id === cityId ? { ...city, location_count: value } : city,
    );
    this.clearMessages();
  };

  removeCity = (cityId: number) => {
    this.cities = this.cities.filter((city) => city.city_id !== cityId);
    this.clearMessages();
  };

  fetchCities = async (searchText: string) => {
    const searchId = ++this.latestCitySearchId;
    this.isCitiesLoading = true;
    this.citiesError = "";
    try {
      const trimmedSearchText = searchText.trim();
      const response = await this.addressAPI.getCity({
        page_no: 1,
        search_text: trimmedSearchText,
        is_metro_city: trimmedSearchText ? undefined : true,
        limit: CITY_SEARCH_LIMIT,
        sort_by: "name",
        sort_type: SortType.ASC,
      });
      if (searchId !== this.latestCitySearchId) return;
      runInAction(() => {
        this.cityOptions = response?.list ?? [];
      });
    } catch (error: any) {
      if (searchId !== this.latestCitySearchId) return;
      runInAction(() => {
        this.cityOptions = [];
        this.citiesError = extractErrorMessage(
          error,
          "Could not load cities. Please try again.",
        );
      });
    } finally {
      if (searchId === this.latestCitySearchId) {
        runInAction(() => {
          this.isCitiesLoading = false;
        });
      }
    }
  };

  private buildDomesticFields = (): DomesticDeliveryFields | string => {
    if (!this.deliveryType) return "Select a domestic delivery type.";

    const range = `${MIN_DELIVERY_LOCATION_COUNT} and ${MAX_DELIVERY_LOCATION_COUNT}`;
    const isHybrid = this.deliveryType === OrderDeliveryType.HYBRID;

    let locationCount: number | null = null;
    if (this.usesRemoteCount) {
      locationCount = parseLocationCount(this.remoteLocationCount);
      if (locationCount === null) {
        return `${isHybrid ? "Remote locations" : "Number of locations"} must be a whole number between ${range}.`;
      }
    }

    let cities: DeliveryCityRequest[] = [];
    if (this.usesCities) {
      if (this.cities.length === 0) {
        return `Select at least one city for ${isHybrid ? "hybrid" : "bulk"} delivery.`;
      }
      const invalidCity = this.cities.find(
        (city) => parseLocationCount(city.location_count) === null,
      );
      if (invalidCity) {
        return `Locations for ${invalidCity.city_name} must be a whole number between ${range}.`;
      }
      cities = this.cities.map((city) => ({
        city_id: city.city_id,
        location_count: parseLocationCount(city.location_count) as number,
      }));
    }

    switch (this.deliveryType) {
      case OrderDeliveryType.REMOTE:
        return {
          delivery_type: OrderDeliveryType.REMOTE,
          location_count: locationCount as number,
        };
      case OrderDeliveryType.BULK:
        return { delivery_type: OrderDeliveryType.BULK, cities };
      case OrderDeliveryType.HYBRID:
        return {
          delivery_type: OrderDeliveryType.HYBRID,
          location_count: locationCount as number,
          cities,
        };
      default:
        return "Select a domestic delivery type.";
    }
  };

  private buildInternationalFields = ():
    | InternationalDeliveryFields
    | string => {
    const range = `${MIN_INTERNATIONAL_DELIVERY_COUNT} and ${MAX_DELIVERY_LOCATION_COUNT}`;
    const remoteCount = parseInternationalCount(this.internationalRemoteCount);
    if (remoteCount === null) {
      return `International remote deliveries must be a whole number between ${range}.`;
    }
    const bulkCount = parseInternationalCount(this.internationalBulkCount);
    if (bulkCount === null) {
      return `International bulk deliveries must be a whole number between ${range}.`;
    }
    if (remoteCount + bulkCount < 1) {
      return "Enter at least one international remote or bulk delivery.";
    }
    return {
      international_remote_count: remoteCount,
      international_bulk_count: bulkCount,
    };
  };

  private buildRequest = (): UpdateOrderDeliveryRequest | string => {
    if (!this.orderId) return "Save the order before adding shipping details.";
    if (!this.deliveryRegion) return "Select a delivery region.";

    const orderId = this.orderId;

    switch (this.deliveryRegion) {
      case OrderDeliveryRegion.DOMESTIC: {
        const domestic = this.buildDomesticFields();
        if (typeof domestic === "string") return domestic;
        return {
          order_id: orderId,
          delivery_region: OrderDeliveryRegion.DOMESTIC,
          ...domestic,
        };
      }
      case OrderDeliveryRegion.INTERNATIONAL: {
        const international = this.buildInternationalFields();
        if (typeof international === "string") return international;
        return {
          order_id: orderId,
          delivery_region: OrderDeliveryRegion.INTERNATIONAL,
          ...international,
        };
      }
      case OrderDeliveryRegion.BOTH: {
        const domestic = this.buildDomesticFields();
        if (typeof domestic === "string") return domestic;
        const international = this.buildInternationalFields();
        if (typeof international === "string") return international;
        return {
          order_id: orderId,
          delivery_region: OrderDeliveryRegion.BOTH,
          ...domestic,
          ...international,
        };
      }
      default:
        return "Select a delivery region.";
    }
  };

  private applySavedDetails = (details: OrderDeliveryDetails) => {
    this.applyDeliveryState({
      region: resolveDeliveryRegion(details),
      type: details.delivery_type,
      remoteLocationCount: details.delivery_location_count,
      cities: details.cities ?? [],
      internationalRemoteCount: details.delivery_international_remote_count,
      internationalBulkCount: details.delivery_international_bulk_count,
    });
  };

  save = async (): Promise<OrderDeliveryDetails | null> => {
    this.clearMessages();
    const request = this.buildRequest();
    if (typeof request === "string") {
      this.saveError = request;
      return null;
    }

    this.isSaving = true;
    try {
      const details = await this.orderAPI.updateOrderDeliveryType(request);
      runInAction(() => {
        if (details) this.applySavedDetails(details);
        this.successMessage = "Shipping details saved.";
      });
      return details ?? null;
    } catch (error: any) {
      runInAction(() => {
        this.saveError = extractErrorMessage(
          error,
          "Could not save shipping details. Please try again.",
        );
      });
      return null;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };
}
